import { Platform } from "react-native";
import ReactNativeAsyncStorage from "@react-native-async-storage/async-storage";
import { getApps, initializeApp, type FirebaseOptions } from "firebase/app";
import {
  browserPopupRedirectResolver,
  getAuth,
  getReactNativePersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  multiFactor,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
} from "firebase/auth";

import { envVariables } from "@/utils/envVariables";

const firebaseConfig: FirebaseOptions = {
  apiKey: envVariables.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: envVariables.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: envVariables.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  messagingSenderId: envVariables.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: envVariables.EXPO_PUBLIC_FIREBASE_APP_ID,
};

export const initializeFirebaseApp = () => {
  if (getApps().length > 0) {
    return;
  }

  const app = initializeApp(firebaseConfig);
  const auth = initializeAuth(app, {
    persistence:
      Platform.OS === "web"
        ? indexedDBLocalPersistence
        : getReactNativePersistence(ReactNativeAsyncStorage),
    // Required on web for signInWithPopup / signInWithRedirect to work.
    // browserPopupRedirectResolver is only present in the browser bundle so
    // it is undefined in the native RN bundle — initializeAuth ignores undefined.
    popupRedirectResolver:
      Platform.OS === "web" ? browserPopupRedirectResolver : undefined,
  });

  auth.tenantId = envVariables.EXPO_PUBLIC_FIREBASE_TENANT_ID;
};

export const checkHasEnrolledMfa = () => {
  const auth = getAuth();
  return Boolean(
    auth.currentUser &&
    multiFactor(auth.currentUser).enrolledFactors.length > 0,
  );
};

/**
 * Returns the raw 10-digit phone number from the user's enrolled MFA factor,
 * or null if MFA is not enrolled.
 */
export const getMfaPhoneNumber = (): string | null => {
  const auth = getAuth();
  if (!auth.currentUser) return null;

  const factors = multiFactor(auth.currentUser).enrolledFactors;
  const phoneFactor = factors.find(
    (f): f is PhoneMultiFactorInfo => f.factorId === "phone",
  );
  if (!phoneFactor?.phoneNumber) return null;

  // Strip non-digit characters, then drop leading 1 if 11 digits (NANP format)
  const digits = phoneFactor.phoneNumber.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return normalized.length === 10 ? normalized : null;
};

export const checkIsSsoUser = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return false;

  const providers = user.providerData.map(p => p.providerId);
  return providers.includes("google.com") || providers.includes("apple.com");
};

export const getLoginMethod = (providers: string[]): string => {
  if (providers.includes("google.com")) return "Google";
  if (providers.includes("apple.com")) return "Apple";
  return "Email";
};

export const createRecaptchaVerifier = (
  containerId: string,
): RecaptchaVerifier => {
  return new RecaptchaVerifier(getAuth(), containerId, { size: "invisible" });
};
